import { useTranslation } from 'react-i18next';
import type { PricingBreakdown } from '@carrierbridge/shared';
import { Truck, Warehouse } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { formatEUR, formatPercent } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { useMarginsEnabled } from '@/hooks/useSettings';

interface QuoteResultCardProps {
  result: PricingBreakdown;
  isBest: boolean;
  diff: number;
  unitLabel: string;
  onClick: () => void;
  simplified?: boolean;
  /** Société qui possède la grille, quand ce n'est pas la nôtre (cotation de groupe). */
  sourceLabel?: string;
}

interface SplitTotals {
  hasMargin: boolean;
  transportPurchase: number;
  transportSales: number;
  sitePurchase: number;
  siteSales: number;
  totalPurchase: number;
  totalSales: number;
}

/**
 * Compute the four-quadrant breakdown (transport / site × purchase / sales)
 * from the pricing result. Falls back gracefully when the backend has not
 * decorated the result with the new `transportPurchase` / `transportSales`
 * fields (e.g. a non-margin quote view) so older payloads keep rendering.
 */
function computeSplits(result: PricingBreakdown): SplitTotals {
  const hasMargin = result.salesPrice != null && result.purchasePrice != null;
  const sitePurchase = Number(result.siteCosts?.total ?? 0);
  const siteSales = Number(result.siteCosts?.billed ?? 0);

  const fallbackTransport =
    Number(result.basePrice ?? 0) +
    Number(result.surchargeTotal ?? 0) +
    Number(result.energySurchargeAmount ?? 0) +
    Number(result.ancillaryTotal ?? 0);
  const transportPurchase = Number(result.transportPurchase ?? fallbackTransport);
  const transportSales = Number(
    result.transportSales
      ?? (hasMargin ? Number(result.salesPrice) - siteSales : transportPurchase),
  );

  const totalPurchase = Number(result.purchasePrice ?? transportPurchase + sitePurchase);
  const totalSales = Number(result.salesPrice ?? totalPurchase);

  return {
    hasMargin,
    transportPurchase: Math.round(transportPurchase * 100) / 100,
    transportSales: Math.round(transportSales * 100) / 100,
    sitePurchase: Math.round(sitePurchase * 100) / 100,
    siteSales: Math.round(siteSales * 100) / 100,
    totalPurchase: Math.round(totalPurchase * 100) / 100,
    totalSales: Math.round(totalSales * 100) / 100,
  };
}

export function QuoteResultCard({ result, isBest, diff, unitLabel, onClick, simplified = false, sourceLabel }: QuoteResultCardProps) {
  const { t } = useTranslation();
  const marginsEnabled = useMarginsEnabled();
  const splits = computeSplits(result);
  const siteCosts = result.siteCosts;
  const hasSite = !!siteCosts;
  const displayPrice = splits.totalSales || result.totalPrice;
  const displayPricePerUnit = result.requestedQuantity > 0
    ? Math.round((displayPrice / result.requestedQuantity) * 100) / 100
    : displayPrice;

  const transportMarginAmount = splits.hasMargin
    ? Math.round((splits.transportSales - splits.transportPurchase) * 100) / 100
    : 0;
  // Taux de marge sur PV (convention backend: vente = achat / (1 - marge)).
  const transportMarginRate = splits.hasMargin && splits.transportSales > 0
    ? Math.round(((splits.transportSales - splits.transportPurchase) / splits.transportSales) * 10000) / 100
    : null;

  // Ajustement de la clause énergie (peut être négatif si la clause répercute une baisse).
  const energyAdjustment = Math.round(Number(result.energySurchargeAmount ?? 0) * 100) / 100;

  const siteMarginPct = Number(result.siteCosts?.marginPct ?? 0);
  const siteMarginAmount = Math.round((splits.siteSales - splits.sitePurchase) * 100) / 100;

  // Simplified view (readonly users with simplified enabled)
  if (simplified) {
    return (
      <div
        className={cn(
          'rounded border bg-surface p-5 transition-all duration-150',
          isBest
            ? 'border-success/40 shadow-card ring-1 ring-success/20'
            : 'border-border/80 shadow-card',
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <h3 className="text-sm font-semibold text-text truncate">{result.carrierName}</h3>
            {sourceLabel && <Badge variant="info" title={t('group.quote.sourceTitle', { owner: sourceLabel })}>{sourceLabel}</Badge>}
            {isBest && <Badge variant="success">{t('quoteResult.bestPrice')}</Badge>}
            {result.deliveryLeadTimeDays != null && (
              <span className="text-xs font-medium text-info">J+{result.deliveryLeadTimeDays}</span>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className={cn('text-2xl font-bold tracking-tight', isBest ? 'text-success' : 'text-text')}>
              {formatEUR(displayPrice)}
            </p>
            <p className="text-xs text-subtle">
              {formatEUR(displayPricePerUnit)}/{unitLabel.toLowerCase()}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        // Les boutons internes gardent leurs propres touches.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'cursor-pointer rounded border bg-surface p-5 transition-all duration-150 hover:shadow-elevated',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        isBest
          ? 'border-success/40 shadow-card ring-1 ring-success/20'
          : 'border-border/80 shadow-card hover:border-border/80',
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-text">{result.carrierName}</h3>
          {sourceLabel && <Badge variant="info" title={t('group.quote.sourceTitle', { owner: sourceLabel })}>{sourceLabel}</Badge>}
          {result.laneFallback && result.laneFallback !== 'DEPARTMENT' && result.laneFallback !== 'EXACT' && result.laneFallback !== 'ZONE' && (
            <Badge variant="warning">{t('quoteResult.fallback')}</Badge>
          )}
          {result.laneFallback === 'ZONE' && (
            <Badge variant="info">{t('quoteResult.zone')}</Badge>
          )}
          {isBest && <Badge variant="success">{t('quoteResult.bestPrice')}</Badge>}
          {result.payantPourApplied && <Badge variant="info">{t('quoteResult.payantPour')}</Badge>}
        </div>
        <div className="text-right flex-shrink-0 ml-4">
          <p className={cn('text-2xl font-bold tracking-tight', isBest ? 'text-success' : 'text-text')}>
            {formatEUR(displayPrice)}
          </p>
          {!isBest && diff > 0 && (
            <p className="text-xs text-subtle mt-0.5">+{formatEUR(diff)}</p>
          )}
          <p className="text-2xs text-subtle mt-0.5">
            {formatEUR(displayPricePerUnit)}/{unitLabel.toLowerCase()}
          </p>
        </div>
      </div>

      {/* Transport / site split */}
      <div className={cn(
        'mt-3 grid gap-2',
        hasSite ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1',
      )}>
        <SplitBlock
          icon={<Truck className="h-3.5 w-3.5" />}
          title={t('quoteResult.transport')}
          subtitle={result.carrierName}
          purchase={splits.transportPurchase}
          sales={splits.transportSales}
          marginAmount={transportMarginAmount}
          marginRate={transportMarginRate}
          marginConfigured={splits.hasMargin}
          showMargin={marginsEnabled}
        />
        {siteCosts && (
          <SplitBlock
            icon={<Warehouse className="h-3.5 w-3.5" />}
            title={t('quoteResult.logisticsSite')}
            subtitle={siteCosts.siteName}
            purchase={splits.sitePurchase}
            sales={splits.siteSales}
            marginAmount={siteMarginAmount}
            marginRate={siteMarginPct}
            marginConfigured={true}
            showMargin={marginsEnabled}
            tone="warning"
          />
        )}
      </div>

      {/* Per-unit + meta */}
      <div className="mt-3 flex items-center gap-3 text-xs text-subtle flex-wrap">
        <span>{t('quoteResult.base')} {formatEUR(result.basePrice)}</span>
        {result.surchargeTotal > 0 && (
          <span>{t('quoteResult.surcharges')} {formatEUR(result.surchargeTotal)}</span>
        )}
        {energyAdjustment !== 0 && (
          <span>
            {t('quoteResult.energy')} {energyAdjustment > 0 ? '+' : ''}{formatEUR(energyAdjustment)}
          </span>
        )}
        {result.ancillaryTotal > 0 && (
          <span>{t('quoteResult.prestations')} {formatEUR(result.ancillaryTotal)}</span>
        )}
        {result.deliveryLeadTimeDays != null && (
          <>
            <span className="h-3 w-px bg-border" />
            <span className="font-medium text-info">J+{result.deliveryLeadTimeDays}</span>
          </>
        )}
      </div>

      {(result.convertedQuantity != null || result.effectiveWeightKg != null) && (
        <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-subtle">
          {result.convertedQuantity != null && (
            <span>{result.requestedQuantity} {result.requestedUnit} = {result.convertedQuantity} {result.bandUnit}</span>
          )}
          {result.effectiveWeightKg != null && (
            <span>{t('quoteResult.weight')} {result.effectiveWeightKg} kg</span>
          )}
          {result.effectivePaletteCount != null && (
            <span>{t('quoteResult.palettes')} {result.effectivePaletteCount}</span>
          )}
        </div>
      )}

      {result.warnings && result.warnings.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1">
          {result.warnings.map((w, i) => (
            <Badge key={i} variant="warning">
              {typeof w === 'string' ? w : w.message}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

interface SplitBlockProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  purchase: number;
  sales: number;
  marginAmount: number;
  marginRate: number | null;
  marginConfigured: boolean;
  /** Org-level toggle: false hides the purchase/sale split and the margin line. */
  showMargin?: boolean;
  tone?: 'default' | 'warning';
}

function SplitBlock({
  icon, title, subtitle, purchase, sales, marginAmount, marginRate, marginConfigured, showMargin = true, tone = 'default',
}: SplitBlockProps) {
  const { t } = useTranslation();
  const showMarginValue = marginConfigured && marginAmount > 0;
  return (
    <div className={cn(
      'rounded border px-3 py-2',
      tone === 'warning' ? 'border-warning/20 bg-warning-soft/30' : 'border-border bg-surface-subtle/50',
    )}>
      <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wide font-semibold text-subtle">
        <span className={tone === 'warning' ? 'text-warning' : 'text-muted'}>{icon}</span>
        {title}
        <span className="font-normal normal-case tracking-normal text-subtle truncate ml-1">{subtitle}</span>
      </div>
      {showMargin ? (
        <>
          <div className="mt-1 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <div>
              <p className="text-2xs uppercase tracking-wide text-subtle">{t('quoteResult.purchase')}</p>
              <p className="font-semibold tabular-nums text-muted">{formatEUR(purchase)}</p>
            </div>
            <div>
              <p className="text-2xs uppercase tracking-wide text-subtle">{t('quoteResult.sale')}</p>
              <p className="font-semibold tabular-nums text-text">{formatEUR(sales)}</p>
            </div>
          </div>
          <div className="mt-1 text-2xs">
            {showMarginValue ? (
              <span className="text-success">
                +{formatEUR(marginAmount)}
                {marginRate != null && marginRate > 0 && ` (${formatPercent(marginRate, { digits: 2 })})`}
              </span>
            ) : marginConfigured ? (
              <span className="text-subtle italic">{t('quoteResult.zeroMargin')}</span>
            ) : (
              <span className="text-subtle italic">{t('quoteResult.noMargin')}</span>
            )}
          </div>
        </>
      ) : (
        <div className="mt-1 text-xs">
          <p className="text-2xs uppercase tracking-wide text-subtle">{t('common.price')}</p>
          <p className="font-semibold tabular-nums text-text">{formatEUR(sales)}</p>
        </div>
      )}
    </div>
  );
}
